import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Button } from '../Button/Button';
import { Badge } from '../Badge/Badge';
import type { ParameterEditorProps, ParameterChange } from './ParameterEditor.types';
import { ParameterEditorSearchBar } from './ParameterEditorSearchBar';
import { ParameterEditorScrollableTabs } from './ParameterEditorScrollableTabs';
import { ParameterEditorFieldList } from './ParameterEditorFieldList';
import { ParameterEditorConfirmPanel } from './ParameterEditorConfirmPanel';
import {
  buildOriginalValues,
  filterCategoryBySearch,
  getCategoryChangeCount,
  getTotalChangeCount,
  buildChangeList,
  buildSavePayload,
} from './ParameterEditor.utils';

const SaveIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
    <path d="M13.75 2H4.5A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V5.25L13.75 2zM10 15.5a2 2 0 110-4 2 2 0 010 4zM12.5 7h-6A.5.5 0 016 6.5v-3a.5.5 0 01.5-.5h6a.5.5 0 01.5.5v3a.5.5 0 01-.5.5z" />
  </svg>
);

const ChevronBackIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
    <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
  </svg>
);

export const ParameterEditor: React.FC<ParameterEditorProps> = ({
  dataSource,
  onSave,
  fetcher,
  title = 'Centro de configuraciones',
  onBack,
  onChange: onChangeExternal,
  onError,
  onSaveSuccess,
  className = '',
  readOnly = false,
  saveButtonText = 'Guardar',
  searchPlaceholder = 'Buscar campo a configurar',
  confirmButtonText = 'Confirmar',
  cancelButtonText = 'Cancelar',
  onResetToDefault,
}) => {
  // ===== STATE =====
  const originalValues = useRef<Map<string, string>>(buildOriginalValues(dataSource));
  const entityDisplayLabels = useRef<Map<string, string>>(new Map());
  const [editedValues, setEditedValues] = useState<Map<string, string>>(new Map());
  const [activeTabId, setActiveTabId] = useState<string>(dataSource[0]?.id ?? '');
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmPanel, setShowConfirmPanel] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Map<string, string>>(new Map());
  const [saving, setSaving] = useState(false);

  // ===== SYNC ORIGINAL VALUES WHEN DATA ACTUALLY CHANGES =====
  // Only rebuild originalValues when real server data changed (e.g. after refetch),
  // NOT when the parent merely recreates the dataSource array with the same content.
  // This prevents pending edits from "disappearing" because the parent updates
  // dataSource in response to the onChange callback.
  useEffect(() => {
    const incoming = buildOriginalValues(dataSource);
    const current = originalValues.current;

    // Check if any value actually changed (different resolvedValue or new/removed params)
    let hasRealChanges = incoming.size !== current.size;
    if (!hasRealChanges) {
      for (const [id, val] of incoming) {
        if (current.get(id) !== val) {
          hasRealChanges = true;
          break;
        }
      }
    }

    if (hasRealChanges) {
      originalValues.current = incoming;
    }
  }, [dataSource]);

  // ===== COMPUTED =====

  // Filter dataSource and parameters by search term
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return dataSource;
    return dataSource
      .map((cat) => filterCategoryBySearch(cat, searchTerm))
      .filter((cat): cat is NonNullable<typeof cat> => cat !== null);
  }, [dataSource, searchTerm]);

  // Build tab items with change badges
  const tabItems = useMemo(() => {
    return filteredCategories.map((cat) => ({
      id: cat.id,
      label: cat.name,
      badge: getCategoryChangeCount(cat, editedValues, originalValues.current) || undefined,
    }));
  }, [filteredCategories, editedValues]);

  // Active category parameters
  const activeCategory = useMemo(() => {
    return filteredCategories.find((c) => c.id === activeTabId);
  }, [filteredCategories, activeTabId]);

  const activeDirectParameters = useMemo(() => {
    if (!activeCategory) return [];
    return activeCategory.parameters ?? [];
  }, [activeCategory]);

  const activeSections = useMemo(() => {
    if (!activeCategory?.sections) return [];
    return activeCategory.sections;
  }, [activeCategory]);

  // Total change count
  const totalChanges = useMemo(
    () => getTotalChangeCount(editedValues, originalValues.current),
    [editedValues]
  );

  // Build change list for confirm panel
  const changeList = useMemo<ParameterChange[]>(
    () => buildChangeList(dataSource, editedValues, originalValues.current, entityDisplayLabels.current),
    [dataSource, editedValues]
  );

  // ===== HANDLERS =====

  const handleFieldChange = useCallback((parameterId: string, value: string) => {
    setEditedValues((prev) => {
      const next = new Map(prev);
      next.set(parameterId, value);
      return next;
    });
    // Clear validation error on change
    setValidationErrors((prev) => {
      if (!prev.has(parameterId)) return prev;
      const next = new Map(prev);
      next.delete(parameterId);
      return next;
    });
    onChangeExternal?.(parameterId, value);
  }, [onChangeExternal]);

  const handleEntityDisplayLabel = useCallback((parameterId: string, label: string) => {
    entityDisplayLabels.current.set(parameterId, label);
  }, []);

  const handleValidationError = useCallback((parameterId: string, error: string) => {
    setValidationErrors((prev) => {
      const next = new Map(prev);
      if (error) {
        next.set(parameterId, error);
      } else {
        next.delete(parameterId);
      }
      return next;
    });
  }, []);

  const handleTabChange = useCallback((id: string) => {
    setActiveTabId(id);
  }, []);

  const handleSearchChange = useCallback(
    (term: string) => {
      setSearchTerm(term);
      // If active tab is no longer in filtered results, switch to first available
      if (term.trim()) {
        const filtered = dataSource
          .map((cat) => filterCategoryBySearch(cat, term))
          .filter((cat): cat is NonNullable<typeof cat> => cat !== null);
        if (filtered.length > 0 && !filtered.some((c) => c.id === activeTabId)) {
          setActiveTabId(filtered[0].id);
        }
      }
    },
    [dataSource, activeTabId]
  );

  const handleOpenConfirm = useCallback(() => {
    // Check for validation errors before opening
    if (validationErrors.size > 0) return;
    setShowConfirmPanel(true);
  }, [validationErrors]);

  const handleUndoChange = useCallback((parameterId: string) => {
    setEditedValues((prev) => {
      const next = new Map(prev);
      next.delete(parameterId);

      // Close panel if no real changes remain
      const hasRealChanges = Array.from(next.entries()).some(
        ([id, val]) => val !== originalValues.current.get(id)
      );
      if (!hasRealChanges) {
        setShowConfirmPanel(false);
      }

      return next;
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (saving) return;
    setSaving(true);

    try {
      const payload = buildSavePayload(changeList);
      await onSave(payload);
      // Update original values immediately with saved values
      // so the UI reflects changes before parent refetches dataSource.
      // When the parent passes new dataSource (with updated rowVersion),
      // the useEffect will rebuild originalValues from the fresh data.
      for (const change of changeList) {
        originalValues.current.set(change.parameterId, change.newValue);
      }
      setEditedValues(new Map());
      setShowConfirmPanel(false);
      onSaveSuccess?.();
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Error al guardar los cambios'));
    } finally {
      setSaving(false);
    }
  }, [saving, changeList, onSave, onSaveSuccess, onError]);

  // ===== EMPTY STATE =====
  if (dataSource.length === 0) {
    return (
      <div
        className={`
          flex items-center justify-center py-16
          text-content-secondary dark:text-dark-content-secondary text-sm
          ${className}
        `.trim().replace(/\s+/g, ' ')}
      >
        No hay categorias de parametros disponibles.
      </div>
    );
  }

  return (
    <div
      className={`
        flex flex-col h-full text-sm
        bg-background-secondary dark:bg-dark-bg-secondary
        overflow-hidden rounded-xl
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-border-primary dark:border-dark-border-primary">
        {/* Left: back + title */}
        <div className="flex items-center gap-1 min-w-0 flex-1">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="shrink-0 p-1 -ml-1 rounded-md text-content-primary dark:text-dark-content-primary hover:bg-[rgba(0,0,0,0.03)] dark:hover:bg-white/5 transition-colors cursor-pointer"
              aria-label="Volver"
            >
              {ChevronBackIcon}
            </button>
          )}
          <div className="flex flex-col min-w-0">
            <h1 className="text-xl font-bold leading-7 tracking-tight text-[#1355b7] dark:text-primary-600 truncate">
              {title}
            </h1>
            {totalChanges > 0 && (
              <div className="mt-1">
                <Badge color="blue" label={`Modificaciones: ${totalChanges}`} />
              </div>
            )}
          </div>
        </div>

        {/* Right: search + save */}
        <div className="flex items-center gap-3 shrink-0">
          <ParameterEditorSearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
          />
          {!readOnly && (
            <Button
              onClick={handleOpenConfirm}
              disabled={totalChanges === 0 || validationErrors.size > 0}
              leftIcon={SaveIcon}
            >
              {saveButtonText}
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable Tabs */}
      {tabItems.length > 0 && (
        <div className="shrink-0">
          <ParameterEditorScrollableTabs
            tabs={tabItems}
            activeId={activeTabId}
            onChange={handleTabChange}
          />
        </div>
      )}

      {/* Field List */}
      <div className="flex-1 overflow-y-auto min-h-0 mx-4 my-3 bg-bg-primary dark:bg-dark-bg-primary rounded-xl">
        {filteredCategories.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-content-secondary dark:text-dark-content-secondary text-sm">
            No se encontraron parametros que coincidan con la busqueda.
          </div>
        ) : (
          <ParameterEditorFieldList
            parameters={activeDirectParameters}
            sections={activeSections}
            editedValues={editedValues}
            originalValues={originalValues.current}
            validationErrors={validationErrors}
            onChange={handleFieldChange}
            onValidationError={handleValidationError}
            onEntityDisplayLabel={handleEntityDisplayLabel}
            fetcher={fetcher}
            readOnly={readOnly}
            onResetToDefault={onResetToDefault}
          />
        )}
      </div>

      {/* Confirm Panel */}
      {showConfirmPanel && (
        <ParameterEditorConfirmPanel
          changes={changeList}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmPanel(false)}
          onUndoChange={handleUndoChange}
          saving={saving}
          confirmButtonText={confirmButtonText}
          cancelButtonText={cancelButtonText}
        />
      )}
    </div>
  );
};
